import {
  faCalendar,
  faPenToSquare,
  faPlus,
  faSearch,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import CustomButton from "../components/customs/custom-button";
import PageLayout from "../components/page-layout";
import axiosInstance from "../utils/axiosInstance";
import CreateAgentModel from "../components/agent-components/create-agent-model";
import { User } from "../providers/auth-provider";
import { agentHeading } from "../components/agent-components/agent-table-data";
import DeleteAgentModel from "../components/agent-components/delete-agent-model";
import PaginationMenu from "../components/pagination/pagination-menu";
import formQueryUrl from "../utils/formQueryUrl";
import DateRangePicker from "../components/date-range-picker";
import { useRangePicker } from "../store/use-range-picker-model";
import clsx from "clsx";
import SkeltonTableRow from "../components/skelton/skelton-table-row";
import UserRow from "../components/agent-components/agent-row";
import NoItem from "../components/no-item";

const Agent = () => {
  const [search, setSearch] = useDebounceValue<string>("", 1000);
  const [model, setModel] = useState<User | boolean>(false);
  const [isDelete, setIsDelete] = useState<number | null>(null);
  const [users, setUsers] = useState<{ totalCount: number; data: User[] }>({
    totalCount: 0,
    data: [],
  });
  const [pageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<Record<string, any>>({});
  const { onToggleRangePicker } = useRangePicker();
  const [menuOpen, setMenuOpen] = useState<number | null>(null);

  // ---------------------------- [ USER DATA ] ----------------------------------------
  useEffect(() => {
    const FindUsers = async () => {
      setLoading(true);
      try {
        const filtersArray = [
          search && { search: search },
          dateRange?.startDate !== null && { startDate: dateRange?.startDate },
          dateRange?.endDate !== null && { endDate: dateRange?.endDate },
        ];
        const res = await axiosInstance.get(
          `/user?page=${page}&limit=${pageSize}${formQueryUrl(filtersArray)}`
        );
        setUsers(res.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    FindUsers();
  }, [
    model,
    isDelete,
    search,
    page,
    pageSize,
    dateRange?.startDate,
    dateRange?.endDate,
  ]);

  // ---------------------------------- [ OPEN AND CLOSE MODEL ] -----------------------------
  const ToggleModel = () => {
    setModel((prev) => (typeof prev === "boolean" ? !prev : false));
  };

  const ToggleDeleteModel = (id?: number) => {
    setIsDelete(id ? id : null);
  };

  // --------------------------------------- [ CALL TO ACTIONS ] -----------------------------
  const tableAction = [
    {
      name: "Edit Agent",
      icon: faPenToSquare,
      actionHandler: (data: User) => setModel(data),
    },
    {
      name: "Delete Agent",
      icon: faTrashCan,
      superAdminOnly: true,
      actionHandler: (data: User) => ToggleDeleteModel(data?.id),
    },
  ];

  // -------------------------------- [ UPDATE CURRENT PAGE ] --------------------------

  const OnChangeCurrentPage = (page) => {
    setPage(page);
  };

  const handleMenuToggle = (rowIndex: number) => {
    setMenuOpen((prevIndex) => (prevIndex === rowIndex ? null : rowIndex));
  };

  return (
    <PageLayout
      pageTitle="Agents"
      primaryAction={
        <>
          <DateRangePicker onChange={(e) => setDateRange(e)}>
            <div
              onClick={onToggleRangePicker}
              className="px-5 py-1 rounded-full appearance-none text-[16px] flex items-center justify-center h-[48px] bg-surface-elevated text-light-gray border border-edge outline-none hover:border-indigo-400/40 transition-all duration-300 cursor-pointer"
            >
              <FontAwesomeIcon icon={faCalendar} />
            </div>
          </DateRangePicker>
          <CustomButton
            className="!rounded-full !font-dmSans !text-[15px] sm:!text-[16px] !font-bold !h-[48px]"
            variant="primary"
            renderLeftIcon={
              <FontAwesomeIcon
                icon={faPlus}
                className="bg-accent/90 text-white text-[14px] rounded-full outline-none p-1"
              />
            }
            onClick={ToggleModel}
          >
            Add Agent
          </CustomButton>
        </>
      }
    >
      {isDelete && (
        <DeleteAgentModel ToggleDeleteModel={ToggleDeleteModel} id={isDelete} />
      )}
      {model && <CreateAgentModel ToggleModel={ToggleModel} isEdit={model} />}
      <div className="bg-surface p-4 sm:p-6 lg:p-8 border mt-8 border-edge rounded-2xl shadow-card animate-fade-in">
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div>
            <h1 className="font-bold leading-[31px] text-text-primary text-[20px] sm:text-[22px]">
              Agent List
            </h1>
            <p className="text-light-gray font-medium text-[14px] sm:text-[16px]">
              All agents
            </p>
          </div>
          <div className="sm:ml-auto w-full sm:w-auto rounded-full flex bg-active-background text-light-gray items-center border border-edge py-2 px-4 focus-within:border-indigo-400/40 focus-within:shadow-glow-sm transition-all duration-300">
            <div className="mr-3 flex items-center text-light-gray">
              <FontAwesomeIcon icon={faSearch} className="w-3 h-3 text-blue" />
            </div>
            <input
              type="text"
              placeholder="Search..."
              className="flex-grow w-full font-dmSans text-sm text-text-primary placeholder:text-placeholder bg-transparent focus:outline-none"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-3">
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 custom-scrollbar">
            <table className={clsx("table-auto h-max w-full text-left")}>
              <thead>
                <tr className="font-medium">
                  {agentHeading.map((data, index) => (
                    <th
                      key={index}
                      className="uppercase text-light-gray font-medium text-[10px] py-2"
                    >
                      {data.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeltonTableRow />
                ) : (
                  users?.data?.map((row, rowIndex: number) => {
                    return (
                      <UserRow
                        key={rowIndex}
                        rowIndex={rowIndex}
                        row={row}
                        handleMenuToggle={handleMenuToggle}
                        menuOpen={menuOpen}
                        tableAction={tableAction}
                        noSpaceAction
                      />
                    );
                  })
                )}
              </tbody>
            </table>
            {!loading && !users?.totalCount && (
              <NoItem
                title="No Users Found"
                desc="There's nothing to see for users"
              />
            )}
          </div>
          <PaginationMenu
            totalCount={users?.totalCount}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={OnChangeCurrentPage}
          />
        </div>
      </div>
    </PageLayout>
  );
};

export default Agent;
